import { FC, useEffect, useLayoutEffect, useState } from 'react'
import { Box, Paper, Link, Button } from '@mui/material'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'
import CheckIcon from '@mui/icons-material/Check'
import { ItemPhoto } from '~/src/components/molecules/ItemPhoto/ItemPhoto'
import { IContainerResponse } from '~/src/repositories/Container/schema/Container'
import { ContainerImagesEditModal } from '~/src/components/organisms/ContainerOrder/Images/ContainerImagesEditModal'
import useSWR from 'swr'
import { containerRepository } from '~/src/repositories'
import { useMessage } from '~/src/hooks'
import { formatDataStringParam } from '~/src/utils/formatDate'

interface IContainerInformationProps {
  id: number
  data?: IContainerResponse['data']['container']
}

export const ContainerInformation: FC<IContainerInformationProps> = ({ id, data }): JSX.Element => {
  const { t } = useTranslation()
  const { handleOpenWithErrorMessage } = useMessage()

  const { data: containerImages, error } = useSWR([`/containers/${id}/images`, id], () =>
    containerRepository.getContainersImages(id)
  )

  useEffect(() => {
    if (error) handleOpenWithErrorMessage('コンテナ画像取得に失敗しました')
  }, [error])

  // コンテナ画像
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false)

  const containerInformations = [
    {
      label: t('container_order_detail.country'),
      value: data?.country,
    },
    {
      label: t('container_order_detail.customer_code'),
      value: data?.customerCode,
    },
    {
      label: t('container_order_detail.proud_first_name'),
      value: data?.companyName,
    },
    {
      label: t('container_order_detail.business_partner_abbreviation'),
      value: data?.customerNameAbbreviation,
    },
    {
      label: t('container_order_detail.customer_name'),
      value: data?.contactPerson,
    },
    {
      label: t('container_order_detail.business_negotiator'),
      value: data?.salesPersonName,
    },
    {
      label: t('container_order_detail.input_date'),
      value: formatDataStringParam(data?.inputDate),
    },
    {
      label: t('container_order_detail.input_charge'),
      value: data?.inputPersonName,
    },
    {
      label: t('container_order_detail.invoice_no.'),
      value: data?.invoiceNo,
    },
    {
      label: t('container_order_detail.container_type'),
      value: data?.containerSize,
    },
    {
      label: t('container_order_detail.container_status'),
      value: data?.cargo,
    },
    {
      label: t('container_order_detail.sales_conditions'),
      value: data?.tradeTerms,
    },
    {
      label: t('container_order_detail.vanning_date'),
      value: formatDataStringParam(data?.vanningDate),
    },
    {
      label: t('container_order_detail.scheduled_shipping_date'),
      value: formatDataStringParam(data?.etd),
    },
    {
      label: t('container_order_detail.estimated_arrival_date'),
      value: formatDataStringParam(data?.eta),
    },
    {
      label: t('container_order_detail.port_of_loading'),
      value: data?.portOfLoading,
    },
    {
      label: t('container_order_detail.landing_port'),
      value: data?.portOfDischarge,
    },
    {
      label: t('container_order_detail.final_destination'),
      value: data?.finalDestination,
    },
    {
      label: t('container_order_detail.consignee'),
      value: data?.consignees,
    },
    {
      label: t('container_order_detail.consignee_contact_information'),
      value: data?.notifyParty,
    },
    {
      label: t('container_order_detail.shipping_company'),
      value: data?.shippingCompany,
    },
    {
      label: t('container_order_detail.ship'),
      value: data?.vessel,
    },
    {
      label: t('container_order_detail.container_no.'),
      value: data?.containerNo,
    },
    {
      label: t('container_order_detail.payment_due'),
      value: formatDataStringParam(data?.paymentDue),
    },
    {
      label: t('container_order_detail.advance_payment'),
      value: (
        <Box
          border={'1px solid rgba(0, 0, 0, 0.23)'}
          display={'flex'}
          borderRadius={'16px'}
          alignItems={'center'}
          paddingX={'10px'}
          paddingY={'3px'}
          fontWeight={400}
        >
          <CheckIcon fontSize={'small'} sx={{ color: 'rgba(0, 0, 0, 0.54)' }} />
          {data?.depositPayment}
        </Box>
      ),
    },
  ]

  // @ts-ignore
  return (
    <Paper sx={{ maxWidth: '550px', marginRight: '20px' }}>
      <Typography
        paddingTop={'16px'}
        paddingLeft={'16px'}
        borderBottom={'1px solid'}
        paddingBottom={'16px'}
        borderColor={'rgba(0, 0, 0, 0.12)'}
        variant="h2"
        gutterBottom
        component="div"
        fontSize={'20px'}
        fontFamily={'Roboto'}
        fontWeight={'Medium'}
        lineHeight={'32px'}
        color={'black'}
      >
        {`${t('container_order_detail.container_information')}`}
      </Typography>
      <Box padding={'16px 0 16px 16px'}>
        {containerInformations.map((item, index) => (
          <Box display={'flex'} padding={'16px'} alignItems={'center'} key={index}>
            <Typography
              color={'black'}
              fontSize={'14px'}
              fontFamily={'Roboto'}
              fontWeight={'Regular'}
              lineHeight={'14px'}
              minWidth={'147px'}
            >
              {item.label}
            </Typography>
            <Typography
              paddingLeft={'32px'}
              fontFamily={'Roboto'}
              fontSize={'14px'}
              fontWeight={'Bold'}
              lineHeight={'14px'}
            >
              {item.value}
            </Typography>
          </Box>
        ))}
        <Box display={'flex'} padding={'16px'}>
          <Typography
            color={'black'}
            fontSize={'14px'}
            fontFamily={'Roboto'}
            fontWeight={'Regular'}
            lineHeight={'14px'}
            minWidth={'147px'}
          >
            {`${t('container_order_detail.container_image')}`}
          </Typography>
          <Box paddingLeft={'16px'}>
            {containerImages && containerImages.data.length > 0 ? (
              <ItemPhoto images={containerImages.data} />
            ) : (
              <>
                <Button
                  sx={{ marginLeft: '16px', transform: 'translateY(-10px)' }}
                  variant="outlined"
                  size="small"
                  onClick={() => setIsEditModalOpen(true)}
                >{`${t('container_order_detail.edit')}`}</Button>
                {isEditModalOpen && (
                  <ContainerImagesEditModal
                    containerImages={containerImages?.data}
                    isOpen={isEditModalOpen}
                    handleClose={() => setIsEditModalOpen(false)}
                  />
                )}
              </>
            )}
          </Box>
        </Box>
        <Typography
          paddingLeft={'16px'}
          fontFamily={'Roboto'}
          fontSize={'14px'}
          color={'black'}
          lineHeight={'14px'}
          fontWeight={'Regular'}
          paddingBottom={'32px'}
        >
          {`${t('container_order_detail.if_you_want_to_track_your_container,_please_use')}`}{' '}
          <Link href="#" underline="always">
            VesselFinder
          </Link>{' '}
          or{' '}
          <Link href="#" underline="always">
            NLDS
          </Link>
        </Typography>
      </Box>
    </Paper>
  )
}
